import type { AppUser } from "../../services/userService";
import type { TodoDto, VorgangDto } from "../../services/vorgangService";
import type { DateRangeValue } from "../redesign/table";
import { beteiligteGruppen, beteiligteUserIds, personenAvatare } from "../vorgang/shared/unifiedRows";
import { deriveVorgangStatus } from "../vorgang/shared/vorgangStatus";

/**
 * Spaltenfilter des Vorgänge-Reiters — CLIENTSEITIG (PO 08.09.2026: „ggf. Filter
 * mit einfügen in die Tabelle im Reiter Vorgänge").
 *
 * Anders als die mandantenweite Vorgangstabelle, die ihre Filter als Query an
 * den Server schickt und seitenweise nachlädt: der Reiter hat die Vorgänge des
 * Kontakts und seines Umfelds bereits vollständig geladen. Ein Server-Filter
 * würde hier nur eine zweite, abweichende Filtersemantik einführen — gefiltert
 * wird deshalb auf den geladenen Daten.
 *
 * Die Werte gelten für den GANZEN Reiter: eigene Tabelle wie Umfeld-Abschnitte.
 * Ein Filter je Abschnitt wäre kaum bedienbar und die Kopfzahlen liefen
 * auseinander.
 */

/** Spalten-Ids, an denen ein Filter hängt (identisch zu den Spalten der Tabelle). */
export const FILTER_SPALTEN = {
    status: "Status",
    betreff: "Betreff",
    beteiligte: "Beteiligte",
    twv: "TWV",
    frist: "Frist",
    ersteller: "Ersteller",
    erstellt: "Erstellt",
} as const;

/** Werte des Status-Dropdowns — wie in der Vorgangstabelle. */
export const STATUS_OPTIONEN = ["Offen", "Erledigt"];

/** Alle Filterwerte des Reiters, nach Filtertyp getrennt (so erwartet sie die DataTable). */
export interface FilterWerte {
    /** text/dropdown je Spalten-Id. */
    text: Record<string, string | boolean | undefined>;
    /** Datumsbereiche je Spalten-Id. */
    datum: Record<string, DateRangeValue | undefined>;
    /** Gewählte User-Ids je Spalten-Id. */
    personen: Record<string, string[] | undefined>;
}

export const LEERE_FILTER: FilterWerte = { text: {}, datum: {}, personen: {} };

/** Wie viele Filter sind gesetzt? (Badge am „Zurücksetzen"-Knopf) */
export function anzahlAktiverFilter(w: FilterWerte): number {
    return (
        Object.values(w.text).filter((v) => v !== undefined && v !== "" && v !== false).length +
        Object.values(w.datum).filter((r) => !!(r?.von || r?.bis)).length +
        Object.values(w.personen).filter((ids) => (ids?.length ?? 0) > 0).length
    );
}

/** Die Felder, auf die der Filter schaut — für Vorgänge und To-dos dieselben. */
export interface FilterFelder {
    status: string;
    /** Was die Betreff-Suche durchsucht: Art UND Titel (der Chip ist Teil des Betreffs). */
    betreff: string;
    beteiligteIds: string[];
    erstellerId: string | null;
    twv?: string | null;
    frist?: string | null;
    createdAt?: string | null;
}

const erstellerId = (rawKey: string | null | undefined, users: AppUser[]): string | null =>
    rawKey ? (personenAvatare([rawKey], users)[0]?.userId ?? null) : null;

export function felderVonVorgang(v: VorgangDto, users: AppUser[]): FilterFelder {
    const gruppen = beteiligteGruppen(users, v.hauptverantwortlich, v.bearbeiter as unknown[], v.zuInformierende);
    return {
        status: deriveVorgangStatus(v),
        betreff: `${v.vorgangsart ?? ""} ${v.titel ?? ""}`,
        beteiligteIds: beteiligteUserIds(gruppen),
        erstellerId: erstellerId(v.createdByUserId ?? v.ownerUserId ?? null, users),
        twv: v.twv,
        frist: v.frist,
        createdAt: v.createdAt,
    };
}

export function felderVonTodo(t: TodoDto, users: AppUser[]): FilterFelder {
    const gruppen = beteiligteGruppen(users, t.hauptverantwortlich, t.bearbeiter as unknown[], t.zuInformierende);
    return {
        status: t.status,
        betreff: t.titel ?? "",
        beteiligteIds: beteiligteUserIds(gruppen),
        erstellerId: erstellerId(t.createdByUserId, users),
        twv: t.twv,
        frist: t.frist,
        createdAt: t.createdAt,
    };
}

/**
 * Kalendertag eines Zeitstempels — LOKAL gerechnet. `toISOString()` wäre falsch:
 * es rechnet nach UTC um und liefert abends den Vortag (dieselbe Falle wie in
 * `table/filterModel.ts`).
 */
function tagVon(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const monat = String(d.getMonth() + 1).padStart(2, "0");
    const tag = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${monat}-${tag}`;
}

/** Liegt der Zeitpunkt im Bereich? Ohne Datum fällt die Zeile aus einem aktiven Bereich heraus. */
function imBereich(iso: string | null | undefined, r: DateRangeValue | undefined): boolean {
    const von = r?.von?.trim() || undefined;
    const bis = r?.bis?.trim() || undefined;
    if (!von && !bis) return true;
    const tag = tagVon(iso);
    if (!tag) return false;
    if (von && tag < von) return false;
    if (bis && tag > bis) return false;
    return true;
}

/** Trifft mindestens eine der gewählten Personen zu? Leere Auswahl = kein Filter. */
function trifftPersonen(ids: string[], gewaehlt: string[] | undefined): boolean {
    if (!gewaehlt?.length) return true;
    return ids.some((id) => gewaehlt.includes(id));
}

export function passtZumFilter(f: FilterFelder, w: FilterWerte): boolean {
    const S = FILTER_SPALTEN;

    const status = w.text[S.status];
    if (typeof status === "string" && status && f.status !== status) return false;

    const betreff = w.text[S.betreff];
    if (typeof betreff === "string" && betreff.trim()) {
        if (!f.betreff.toLowerCase().includes(betreff.trim().toLowerCase())) return false;
    }

    if (!trifftPersonen(f.beteiligteIds, w.personen[S.beteiligte])) return false;
    if (!trifftPersonen(f.erstellerId ? [f.erstellerId] : [], w.personen[S.ersteller])) return false;

    if (!imBereich(f.twv, w.datum[S.twv])) return false;
    if (!imBereich(f.frist, w.datum[S.frist])) return false;
    if (!imBereich(f.createdAt, w.datum[S.erstellt])) return false;

    return true;
}

/**
 * Gefiltert wird auf VORGANGS-Ebene: die To-dos eines Treffers bleiben als
 * Kindzeilen vollständig an ihrem Vorgang hängen. Sie einzeln wegzufiltern
 * würde den Fortschritt („2 von 5") gegen die sichtbaren Zeilen laufen lassen.
 */
export function filtereVorgaenge(vorgaenge: VorgangDto[], w: FilterWerte, users: AppUser[]): VorgangDto[] {
    if (anzahlAktiverFilter(w) === 0) return vorgaenge;
    return vorgaenge.filter((v) => passtZumFilter(felderVonVorgang(v, users), w));
}

/** Dieselben Werte auf die elternlosen To-dos unter „Weitere Vorgänge". */
export function filtereTodos(todos: TodoDto[], w: FilterWerte, users: AppUser[]): TodoDto[] {
    if (anzahlAktiverFilter(w) === 0) return todos;
    return todos.filter((t) => passtZumFilter(felderVonTodo(t, users), w));
}
